import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheckIcon, CurrencyDollarIcon, HeartIcon, BeakerIcon, ClipboardDocumentCheckIcon,
    ChevronDownIcon, DocumentMagnifyingGlassIcon, CogIcon, PlayIcon, AdjustmentsHorizontalIcon,
    UsersIcon, BriefcaseIcon, CommandLineIcon, ChartBarIcon, ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';


// --- Data for the page ---
const stlcPhases = [
    { icon: DocumentMagnifyingGlassIcon, name: 'Requirement Analysis', description: 'Understand and analyze requirements from a testing perspective.' },
    { icon: CogIcon, name: 'Test Planning', description: 'Define the test strategy, scope, resources, and schedule in the Test Plan.' },
    { icon: ClipboardDocumentCheckIcon, name: 'Test Case Development', description: 'Create detailed, step-by-step test cases based on requirements.' },
    { icon: AdjustmentsHorizontalIcon, name: 'Test Environment Setup', description: 'Configure the necessary hardware, software, and network for testing.' },
    { icon: PlayIcon, name: 'Test Execution', description: 'Execute the prepared test cases and log any defects found.' },
    { icon: ShieldCheckIcon, name: 'Test Cycle Closure', description: 'Evaluate exit criteria and create test summary reports.' }
];

const testingPrinciples = [
    { title: '1. Testing shows the presence of defects, not their absence.', content: 'Testing can reveal that defects are present, but it cannot prove that there are no defects. Even after thorough testing, we cannot say a product is 100% defect-free.' },
    { title: '2. Exhaustive testing is impossible.', content: 'Testing every possible input and precondition is not feasible. Instead, risk analysis and prioritization are used to focus testing efforts.' },
    { title: '3. Early testing saves time and money.', content: 'To find defects early, testing must start as early as possible in the SDLC. It is much cheaper to fix a defect in the early stages.' },
    { title: '4. Defects cluster together (Pareto Principle).', content: 'A small number of modules usually contain most of the defects discovered (the 80/20 Rule).' },
    { title: '5. Beware of the pesticide paradox.', content: 'If the same tests are repeated, they will eventually no longer find new bugs. Test cases need to be regularly reviewed and revised.' },
    { title: '6. Testing is context-dependent.', content: 'The way you test an e-commerce site is different from how you test a mobile game. The context determines the testing approach.' },
    { title: '7. Absence-of-errors is a fallacy.', content: 'Finding and fixing defects does not help if the system is unusable and does not fulfill the users’ needs. A bug-free but useless product is still a failure.' }
];

const functionalTestingTypes = [
    { name: 'Unit Testing', description: 'Testing individual components in isolation.' },
    { name: 'Integration Testing', description: 'Testing how modules work together as a group.' },
    { name: 'System Testing', description: 'Testing the complete, integrated software product.' },
    { name: 'Acceptance Testing', description: 'Verifying the system for acceptability by the end-user.' },
    { name: 'Regression Testing', description: 'Ensuring new changes have not broken existing features.' },
    { name: 'Smoke Testing', description: 'A preliminary test to reveal simple failures severe enough to reject a release.' },
];

const nonFunctionalTestingTypes = [
    { name: 'Performance Testing', description: 'Testing responsiveness and stability under a workload.' },
    { name: 'Load Testing', description: 'Simulating a high number of concurrent users.' },
    { name: 'Security Testing', description: 'Uncovering vulnerabilities and protecting data.' },
    { name: 'Usability Testing', description: 'Evaluating how easy and user-friendly an application is.' },
    { name: 'Compatibility Testing', description: 'Testing across different browsers, devices, and OS.' },
    { name: 'Scalability Testing', description: 'Testing a system\'s ability to scale up or out.' },
];

const testArtifacts = [
    { name: 'Test Plan', description: 'A strategic document outlining the scope, approach, resources, and schedule of testing activities.' },
    { name: 'Test Scenario', description: 'A high-level description of a feature to be tested or an end-to-end user flow.' },
    { name: 'Test Case', description: 'A detailed set of step-by-step instructions to verify a specific requirement.' },
    { name: 'Bug Report', description: 'A detailed document that reports a defect, including steps to reproduce it.' },
    { name: 'Traceability Matrix', description: 'A table that maps requirements to test cases to ensure full test coverage.' },
    { name: 'Test Summary Report', description: 'A document summarizing the results of a testing cycle, including metrics and a final assessment.' }
];

const blackBoxTechniques = [
    { name: 'Equivalence Partitioning', description: 'A technique that divides input data into partitions of equivalent data from which test cases can be derived. If one condition in a partition works, all are assumed to work.' },
    { name: 'Boundary Value Analysis (BVA)', description: 'A technique that focuses on testing the "boundaries" between partitions. It tests values at the minimum, just above minimum, maximum, and just below maximum.' },
    { name: 'Decision Table Testing', description: 'A method used to test complex business logic by creating a table of conditions and the resulting actions, ensuring all combinations are considered.' },
    { name: 'State Transition Testing', description: 'This technique is used to test the behavior of a system as it moves between different states in response to events or inputs.' }
];

const whiteBoxTechniques = [
    { name: 'Statement Coverage', description: 'A technique that aims to execute every single statement in the source code at least once during testing.' },
    { name: 'Branch Coverage', description: 'This technique ensures that every possible branch from each decision point (e.g., if-else statements) is executed at least once.' },
    { name: 'Path Coverage', description: 'A more thorough technique that aims to execute every possible independent path through the code from entry to exit.' }
];

// --- Animation Variants (Consistent across the page) ---
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

const cardHover = {
    y: -10,
    boxShadow: "0px 20px 30px rgba(0, 0, 0, 0.1)",
    transition: { type: "spring", stiffness: 300 }
};

// --- Helper Components for Interactive Elements ---
const AccordionItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <motion.div className="border-b border-gray-200" variants={itemVariants}>
            <button
                className="w-full flex justify-between items-center py-4 text-left font-semibold text-lg text-gray-800"
                onClick={() => setIsOpen(!isOpen)}
            >
                {item.title || item.name}
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }}><ChevronDownIcon className="w-5 h-5 text-gray-500" /></motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <p className="pb-4 pr-8 text-gray-600">{item.content || item.description}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const TabbedInterface = ({ tabs }) => {
    const [activeTab, setActiveTab] = useState(Object.keys(tabs)[0]);
    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex justify-center border-b border-gray-200 mb-6">
                {Object.keys(tabs).map(tabName => (
                    <button
                        key={tabName}
                        onClick={() => setActiveTab(tabName)}
                        className={`px-6 py-2 font-semibold capitalize ${activeTab === tabName ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
                    >
                        {tabName.replace('_', ' ')}
                    </button>
                ))}
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                >
                    {tabs[activeTab]}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

// --- Main Page Component ---
const AboutQA = () => {
    return (
        <div className="space-y-24">
            {/* Page Header */}
            <motion.div className="text-center" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">The World of Quality Assurance</h1>
                <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">A definitive guide to the core principles, cycles, and methodologies that ensure software quality.</p>
            </motion.div>

            {/* --- NEW: Testing Methodologies Section --- */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">Development Methodologies & QA's Role</h2>
                    <p className="mt-3 max-w-3xl mx-auto text-lg text-gray-600">Testing is not one-size-fits-all; it adapts to the development model.</p>
                </div>
                <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={staggerContainer}>
                    <motion.div className="bg-white p-6 rounded-xl shadow-lg" variants={itemVariants}>
                        <h3 className="font-bold text-xl text-indigo-600">Waterfall Model</h3>
                        <p className="text-sm mt-2 text-gray-600">A linear, sequential approach where each phase must be completed before the next begins. **QA Role:** Testing is a distinct phase at the end, making defects expensive to fix.</p>
                    </motion.div>
                    <motion.div className="bg-white p-6 rounded-xl shadow-lg" variants={itemVariants}>
                        <h3 className="font-bold text-xl text-indigo-600">V-Model</h3>
                        <p className="text-sm mt-2 text-gray-600">An extension of Waterfall where testing activities are planned in parallel with development phases. **QA Role:** Early test planning (e.g., acceptance tests are planned during requirements).</p>
                    </motion.div>
                    <motion.div className="bg-white p-6 rounded-xl shadow-lg" variants={itemVariants}>
                        <h3 className="font-bold text-xl text-indigo-600">Agile Model (Scrum)</h3>
                        <p className="text-sm mt-2 text-gray-600">An iterative approach where work is done in short cycles called Sprints. **QA Role:** QA is a continuous activity, involved throughout the sprint from planning to delivery.</p>
                    </motion.div>
                </motion.div>
            </motion.section>

            {/* The Software Testing Life Cycle (STLC) */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">The Software Testing Life Cycle (STLC)</h2>
                    <p className="mt-3 max-w-3xl mx-auto text-lg text-gray-600">STLC is a structured process that outlines every task in the testing of a software product.</p>
                </div>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" variants={staggerContainer}>
                    {stlcPhases.map(phase => (
                        <motion.div key={phase.name} className="bg-white p-6 rounded-xl shadow-lg text-center" variants={itemVariants} whileHover={cardHover}>
                            <phase.icon className="w-12 h-12 mx-auto text-indigo-600"/>
                            <h3 className="mt-5 text-xl font-semibold">{phase.name}</h3>
                            <p className="mt-2 text-gray-600 text-sm">{phase.description}</p>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* --- NEW: Key Test Artifacts Section --- */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">Key Test Artifacts</h2>
                    <p className="mt-3 max-w-3xl mx-auto text-lg text-gray-600">The tangible documents and outputs created during the testing process.</p>
                </div>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer}>
                    {testArtifacts.map(artifact => (
                        <motion.div key={artifact.name} className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500" variants={itemVariants}>
                            <p className="font-bold text-indigo-800">{artifact.name}</p>
                            <p className="text-indigo-700 text-sm">{artifact.description}</p>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* The Testing Pyramid */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">The Testing Pyramid</h2>
                    <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-600">A framework that helps structure testing efforts for maximum efficiency.</p>
                </div>
                <motion.div className="max-w-md mx-auto space-y-1" variants={staggerContainer}>
                    <motion.div variants={itemVariants} className="bg-red-400 text-white text-center p-3 rounded-t-lg shadow-lg">
                        <p className="font-bold">UI / E2E Tests</p><p className="text-xs">(Slow & Expensive)</p>
                    </motion.div>
                    <motion.div variants={itemVariants} className="bg-yellow-400 text-white text-center p-6 shadow-lg">
                        <p className="font-bold">Integration / API Tests</p>
                    </motion.div>
                    <motion.div variants={itemVariants} className="bg-green-500 text-white text-center p-10 rounded-b-lg shadow-lg">
                        <p className="font-bold text-lg">Unit Tests</p><p className="text-sm">(Fast & Cheap)</p>
                    </motion.div>
                </motion.div>
            </motion.section>

            {/* Interactive Tabs for Testing Types */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Common Types of Testing</h2>
                <TabbedInterface tabs={{
                    'functional': <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{functionalTestingTypes.map(type => (<div key={type.name} className="bg-white p-4 rounded-lg shadow-md border border-gray-200"><p className="font-bold text-gray-800">{type.name}</p><p className="text-gray-600 text-sm">{type.description}</p></div>))}</div>,
                    'non-functional': <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{nonFunctionalTestingTypes.map(type => (<div key={type.name} className="bg-white p-4 rounded-lg shadow-md border border-gray-200"><p className="font-bold text-gray-800">{type.name}</p><p className="text-gray-600 text-sm">{type.description}</p></div>))}</div>
                }} />
            </motion.section>

            {/* --- NEW: Interactive Testing Techniques Section --- */}
            <motion.section variants={sectionVariants} initial="visible" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Test Design Techniques</h2>
                <TabbedInterface tabs={{
                    'Black-Box': <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6"><motion.div variants={staggerContainer}>{blackBoxTechniques.map(technique => (<AccordionItem key={technique.name} item={technique} />))}</motion.div></div>,
                    'White-Box': <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6"><motion.div variants={staggerContainer}>{whiteBoxTechniques.map(technique => (<AccordionItem key={technique.name} item={technique} />))}</motion.div></div>
                }} />
            </motion.section>

            {/* Accordion for 7 Principles of Testing */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">The 7 Core Principles of Testing</h2>
                    <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-600">These fundamental axioms guide the entire testing process and strategy.</p>
                </div>
                <motion.div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6" variants={staggerContainer}>
                    {testingPrinciples.map(principle => (<AccordionItem key={principle.title} item={principle} />))}
                </motion.div>
            </motion.section>

            {/* --- NEW: QA in DevOps Section --- */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">QA in the World of DevOps</h2>
                    <p className="mt-3 max-w-3xl mx-auto text-lg text-gray-600">In modern development, quality is a shared responsibility, not a final gate.</p>
                </div>
                <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <motion.div variants={itemVariants}>
                        <h3 className="font-bold text-xl text-indigo-600">Shift-Left Testing</h3>
                        <p className="mt-2 text-gray-600">The practice of moving testing activities earlier in the development lifecycle. By involving QA in requirements and design, and by running automated tests in the CI/CD pipeline, teams can find and fix defects sooner and more cheaply.</p>
                    </motion.div>
                    <motion.div variants={itemVariants} className="bg-gray-100 p-4 rounded-lg text-center font-mono text-sm text-gray-700">
                        <p>Plan &rarr; Code &rarr; <span className="font-bold text-indigo-600">Build & Test</span> &rarr; Release &rarr; <span className="font-bold text-indigo-600">Deploy & Test</span> &rarr; Operate &rarr; <span className="font-bold text-indigo-600">Monitor</span></p>
                        <p className="text-xs mt-2">(QA is involved at every stage)</p>
                    </motion.div>
                </div>
            </motion.section>

            {/* --- NEW: QA Career Path --- */}
            <motion.section variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900">The QA Career Path</h2>
                    <p className="mt-3 max-w-3xl mx-auto text-lg text-gray-600">A career in QA offers significant growth opportunities.</p>
                </div>
                <div className="flex justify-between items-center max-w-4xl mx-auto">
                    {[{icon: UsersIcon, role: 'QA Analyst'}, {icon: CommandLineIcon, role: 'Automation Engineer / SDET'}, {icon: BriefcaseIcon, role: 'QA Lead'}, {icon: ArrowTrendingUpIcon, role: 'QA Manager'}].map((item, index, arr) => (
                        <React.Fragment key={item.role}>
                            <motion.div className="text-center" variants={itemVariants}>
                                <item.icon className="w-10 h-10 mx-auto text-indigo-600 bg-indigo-100 p-2 rounded-full"/>
                                <p className="mt-2 font-semibold">{item.role}</p>
                            </motion.div>
                            {index < arr.length - 1 && <motion.div className="flex-grow h-0.5 bg-gray-300 mx-4" variants={itemVariants}/>}
                        </React.Fragment>
                    ))}
                </div>
            </motion.section>

            {/* Call to Action Section */}
            <motion.section className="text-center py-12 bg-indigo-600 rounded-lg text-white" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.5 }}>
                <h2 className="text-3xl font-bold">Feeling Empowered?</h2>
                <p className="mt-2 max-w-xl mx-auto">You've covered the core theory. Now, let's put it into practice.</p>
                <Link to="/getting-started" className="mt-8 inline-block bg-white text-indigo-700 px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transform hover:scale-105 transition-transform duration-300 shadow-lg">
                    See the Full Roadmap
                </Link>
            </motion.section>
        </div>
    );
};

export default AboutQA;